import React from 'react';
import styled from 'styled-components';
import {Detail, Heading, Icon, Text} from '@forecast-it/design-system';
import {FeatureFlagDetailData} from './featureFlagDetailData';
import {FeatureFlagRolloutBar} from './FeatureFlagRolloutBar';
import {FEATURE_FLAG_CARD_BORDER, featureFlagCardSurface} from './featureFlagSurface';

const Cards = styled.div`
	display: grid;
	/* The rollout card carries a bar and two notes; the other two carry a single number each. */
	grid-template-columns: 1.6fr 1fr 1fr;
	gap: 12px;
	margin-top: 12px;
`;

const Card = styled.div`
	${featureFlagCardSurface}
	padding: 16px 20px;
`;

const CardHead = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
`;

/**
 * A card's heading, set small and wide-tracked so it reads as a label rather than as a line of copy.
 *
 * The tracking has to be declared on the child rather than here: the typography components emit their
 * own letter-spacing onto the element they render, and a declaration on that element beats one
 * inherited from this wrapper.
 */
const MicroLabel = styled.span`
	> * {
		text-transform: uppercase;
		letter-spacing: 0.6px;
	}
`;

const PercentRow = styled.div`
	display: flex;
	align-items: baseline;
	gap: 8px;
	margin: 8px 0 10px;
`;

const Notes = styled.div`
	display: flex;
	gap: 8px;
	margin-top: 14px;
	padding-top: 12px;
	border-top: 1px solid ${FEATURE_FLAG_CARD_BORDER};
`;

const NoteIcon = styled.span`
	flex: none;
	margin-top: 1px;
`;

const NoteText = styled.span`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

const StatValue = styled.div`
	margin-top: 8px;
`;

const ineligibleNote = (count: number): string =>
	count === 1
		? '1 grant is to a company outside the eligible pool (trial, free-forever, or access-blocked) and does not count toward the percentage.'
		: `${count} grants are to companies outside the eligible pool (trials, free-forever, or access-blocked) and don't count toward the percentage.`;

export interface FeatureFlagRolloutSummaryProps {
	detail: FeatureFlagDetailData;
}

export function FeatureFlagRolloutSummary({detail}: FeatureFlagRolloutSummaryProps): JSX.Element {
	return (
		<Cards>
			<Card>
				<CardHead>
					<MicroLabel>
						<Detail size="2">CURRENT ROLLOUT</Detail>
					</MicroLabel>
					<Detail>{`${detail.eligibleGranted} of ${detail.eligibleTotal} eligible companies`}</Detail>
				</CardHead>
				<PercentRow>
					<Heading size="2">{detail.rolloutPercentLabel}</Heading>
					<Text>of eligible companies (excludes trials &amp; free-forever)</Text>
				</PercentRow>
				<FeatureFlagRolloutBar percent={detail.rolloutPercent} label={detail.rolloutPercentLabel} height="8px" />
				<Notes>
					<NoteIcon>
						<Icon size="s" icon="info" />
					</NoteIcon>
					<NoteText>
						<Detail color="medium">
							{`100% covers today's eligible companies only — new companies still won't get the flag. ` +
								`Marking a flag as rolled out is a code change: ask a developer on ${detail.teamLabel} ` +
								`to set it to rolled out.`}
						</Detail>
						{detail.ineligibleGrantCount > 0 ? (
							<Detail color="medium">{ineligibleNote(detail.ineligibleGrantCount)}</Detail>
						) : null}
					</NoteText>
				</Notes>
			</Card>
			<Card>
				<MicroLabel>
					<Detail size="2">COMPANIES</Detail>
				</MicroLabel>
				<StatValue>
					<Heading size="2">{detail.companyCount}</Heading>
				</StatValue>
			</Card>
			<Card>
				<MicroLabel>
					<Detail size="2">INDIVIDUAL USERS</Detail>
				</MicroLabel>
				<StatValue>
					<Heading size="2">{detail.userCount}</Heading>
				</StatValue>
			</Card>
		</Cards>
	);
}
